import gsap from "gsap"

// Side-effect import: executes the IIFE, registers window.Peel
import "../vendor/peel.js"

// ── Peel config ────────────────────────────────────────────────────────────
// All values are in px. xOffset = distance of the peel tip from the right
// edge; y = distance from the top edge. Larger values = more paper peeled.
const PEEL_REST_X_OFFSET = 280 // resting peek after intro
const PEEL_REST_Y = 200
const PEEL_HOVER_X_OFFSET = 380 // peek while hovering the corner zone
const PEEL_HOVER_Y = 320
// Mobile (≤ 768px) — smaller peel to preserve content readability
const PEEL_MOBILE_BREAKPOINT = 768
const PEEL_REST_X_OFFSET_MOBILE = 100
const PEEL_REST_Y_MOBILE = 70
// ──────────────────────────────────────────────────────────────────────────

const block = ".takeover"

const writeToTopLevelDom = (element) => {
  element.remove()
  document.body.appendChild(element)
  return element
}

const currentDate = new Date().toISOString()

export function Takeover() {
  const elements = document.querySelectorAll(block)

  elements.forEach((el) => {
    // ── Date-range guard ─────────────────────────────────────────
    const startDate = el.dataset.startDate
    if (startDate && currentDate < startDate) {
      el.remove()
      return
    }

    const endDate = el.dataset.endDate
    if (endDate && currentDate > endDate) {
      el.remove()
      return
    }

    // ── 24h localStorage cooldown ────────────────────────────────
    const id = el.id
    const lsKey = `takeover-${id}-seen`
    const takeoverStored = localStorage.getItem(lsKey)
    let date = takeoverStored ? new Date(takeoverStored) : null

    if (date) {
      const diffTime = Math.abs(new Date(currentDate) - date)
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

      if (diffDays > 1) {
        localStorage.removeItem(lsKey)
      } else {
        el.remove()
        return
      }
    }

    // ── DOM setup ────────────────────────────────────────────────
    const element = writeToTopLevelDom(el)
    const closeBtn = element.querySelector(`${block}__close-btn`)
    const topLayer = element.querySelector(`${block}__layer--top`)
    const backLayer = element.querySelector(`${block}__layer--back`)
    const bottomLayer = element.querySelector(`${block}__layer--bottom`)

    let peel = null
    let resizeTimer = null
    let isClosed = false

    // Interaction state
    let isDragging = false
    let inCornerZone = false
    let peelTween = null

    // Live proxy position — GSAP animates x/y here; we read it in onUpdate
    const peelProxy = { x: 0, y: 0 }

    // Stored cleanup for document-level listeners added after intro
    let cleanupDocListeners = null

    // ── Close handler ────────────────────────────────────────────
    const handleCloseClick = () => {
      if (isClosed) return
      isClosed = true

      // Detach interaction listeners immediately so nothing interferes
      if (peelTween) peelTween.kill()
      if (cleanupDocListeners) cleanupDocListeners()
      window.removeEventListener("resize", handleResize)

      const teardown = () => {
        if (peel) {
          peel.removeEvents()
          peel = null
        }
        element.remove()
        document.body.style.overflow = ""
        document.body.classList.remove("takeover-active")
        localStorage.setItem(lsKey, new Date().toISOString())
      }

      if (!peel) {
        teardown()
        return
      }

      // Exit animation: peel away to the bottom-left (reverse of entrance)
      const h = element.offsetHeight
      const w = element.offsetWidth
      gsap.to(peelProxy, {
        x: -w,
        y: h * 2,
        duration: 0.9,
        ease: "power2.in",
        onUpdate: () => peel && peel.setPeelPosition(peelProxy.x, peelProxy.y),
        onComplete: teardown,
      })
    }

    closeBtn.addEventListener("click", handleCloseClick, { once: true })
    closeBtn.addEventListener("touchend", handleCloseClick, {
      once: true,
      passive: true,
    })

    // ── Resize: recreate peel with updated dimensions ─────────────
    const handleResize = () => {
      clearTimeout(resizeTimer)
      resizeTimer = setTimeout(() => {
        if (peel && !isClosed) {
          if (cleanupDocListeners) cleanupDocListeners()
          peel.removeEvents()
          peel = null
          initPeel()
        }
      }, 200)
    }

    // ── Show after 2s delay, then initialise peel.js ─────────────
    const isHidden = element.classList.contains("hide")

    if (isHidden) {
      setTimeout(() => {
        if (isClosed) return
        element.classList.remove("hide")
        element.classList.add("show")
        document.body.style.overflow = "hidden"
        document.body.classList.add("takeover-active")
        initPeel()
      }, 2000)
    } else {
      initPeel()
    }

    // ── peel.js factory ──────────────────────────────────────────
    function createPeelInstance() {
      return new window.Peel(element, {
        corner: window.Peel.Corners.TOP_RIGHT,
        topElement: topLayer,
        backElement: backLayer,
        bottomElement: bottomLayer,
        // Prevent peel.js from calling preventDefault on touchstart, which
        // suppresses click synthesis and makes the close button unresponsive on mobile.
        dragPreventsDefault: false,

        // ------------
        // Back Reflection
        // ------------
        backReflection: true,
        backReflectionSize: 0.05,
        // backReflectionOffset: 1,
        backReflectionAlpha: 0.8,
        // backReflectionDistribute: true,

        // ------------
        // top shadow
        // ------------
        topShadow: true,
        topShadowBlur: 6,
        topShadowAlpha: 0.3,
        topShadowOffsetX: -14,
        topShadowOffsetY: -10,
        topShadowCreatesShape: true,

        // ------------
        // back shadow
        // ------------
        backShadow: true,
        backShadowSize: 0.65,
        // backShadowOffset: .5,
        // backShadowAlpha: 1,
        // backShadowDistribute: true,

        // ------------
        // bottom shadow
        // ------------
        bottomShadow: true,
        // bottomShadowSize: 1.5,
        // bottomShadowOffset: 0,
        bottomShadowDarkAlpha: 0.2,
        bottomShadowLightAlpha: 0.55,
        // bottomShadowDistribute: true,
      })
    }

    // Smoothly animate the peel to a target {x, y} position
    function animatePeelTo(target, duration = 0.35, ease = "power2.out") {
      if (peelTween) peelTween.kill()
      peelTween = gsap.to(peelProxy, {
        x: target.x,
        y: target.y,
        duration,
        ease,
        onUpdate: () => peel && peel.setPeelPosition(peelProxy.x, peelProxy.y),
      })
    }

    // ── Interaction handlers (attached after intro settles) ───────
    function attachInteractionHandlers(restPos, hoverPos) {
      // Returns true when the cursor is in the top-right 20% corner zone.
      // The element is fixed 100vw×100vh at (0,0), so clientX/Y === element coords.
      const inZone = (clientX, clientY) =>
        clientX > element.offsetWidth * 0.7 &&
        clientY < element.offsetHeight * 0.3

      // Hover: while in the corner zone, X stays at hoverPos.x and Y tracks
      // the mouse directly. On exit, animate smoothly back to restPos.
      const onMouseMove = (e) => {
        if (isDragging) return
        const nowIn = inZone(e.clientX, e.clientY)

        if (nowIn) {
          inCornerZone = true
          if (peelTween) peelTween.kill()
          peelProxy.x = e.clientX
          peelProxy.y = e.clientY
          if (peel) peel.setPeelPosition(e.clientX, e.clientY)
        } else if (inCornerZone) {
          inCornerZone = false
          animatePeelTo(restPos)
        }
      }

      const onMouseLeave = () => {
        if (isDragging || !inCornerZone) return
        inCornerZone = false
        animatePeelTo(restPos)
      }

      // peel.js fires its drag callback on mousedown (to set initial position)
      // as well as on mousemove. Without a threshold, any click immediately
      // calls setPeelPosition(clickX, clickY) and peels the whole page away.
      // We record the mousedown anchor and ignore drag callbacks until the
      // cursor has actually moved beyond DRAG_THRESHOLD pixels.
      const DRAG_THRESHOLD = 8
      let dragAnchor = null

      const onMouseDown = (e) => {
        dragAnchor = { x: e.clientX, y: e.clientY }
      }

      // Drag: free-form peel follows the cursor from anywhere on the takeover.
      // x/y from peel.js ≈ clientX/Y because the element is fixed at (0,0)
      // with body overflow:hidden (so scrollX/Y = 0).
      peel.handleDrag(function (evt, x, y) {
        if (dragAnchor) {
          if (
            Math.abs(x - dragAnchor.x) < DRAG_THRESHOLD &&
            Math.abs(y - dragAnchor.y) < DRAG_THRESHOLD
          )
            return
          dragAnchor = null // threshold crossed — commit to drag
        }
        isDragging = true
        if (peelTween) peelTween.kill()
        peelProxy.x = x
        peelProxy.y = y
        this.setPeelPosition(x, y)
      })

      // On drag release: snap the peel back to rest or hover position
      const onMouseUp = (e) => {
        dragAnchor = null
        if (!isDragging) return
        isDragging = false
        const nowIn = inZone(e.clientX, e.clientY)
        inCornerZone = nowIn
        animatePeelTo(nowIn ? hoverPos : restPos, 0.4, "power2.out")
      }

      // Escape key closes the takeover
      const onKeyDown = (e) => {
        if (e.key === "Escape") handleCloseClick()
      }

      element.addEventListener("mousedown", onMouseDown)
      element.addEventListener("mousemove", onMouseMove)
      element.addEventListener("mouseleave", onMouseLeave)
      document.addEventListener("mouseup", onMouseUp)
      document.addEventListener("keydown", onKeyDown)

      // Store cleanup so resize / close can tear these down
      cleanupDocListeners = () => {
        element.removeEventListener("mousedown", onMouseDown)
        element.removeEventListener("mousemove", onMouseMove)
        element.removeEventListener("mouseleave", onMouseLeave)
        document.removeEventListener("mouseup", onMouseUp)
        document.removeEventListener("keydown", onKeyDown)
        cleanupDocListeners = null
      }
    }

    // ── peel.js initialisation ───────────────────────────────────
    function initPeel() {
      if (!window.Peel) return

      window.addEventListener("resize", handleResize)

      const w = element.offsetWidth
      const h = element.offsetHeight
      const isMobile = w <= PEEL_MOBILE_BREAKPOINT
      const restPos = {
        x: w - (isMobile ? PEEL_REST_X_OFFSET_MOBILE : PEEL_REST_X_OFFSET),
        y: isMobile ? PEEL_REST_Y_MOBILE : PEEL_REST_Y,
      }
      const hoverPos = { x: w - PEEL_HOVER_X_OFFSET, y: PEEL_HOVER_Y }

      peel = createPeelInstance()

      // ── Intro animation ──────────────────────────────────────────
      // Start fully peeled (tip at the bottom-left corner) then roll
      // back to the resting position so the page appears to unfurl.
      peelProxy.x = -w
      peelProxy.y = h * 2
      peel.setPeelPosition(0, h)

      gsap.to(peelProxy, {
        x: restPos.x,
        y: restPos.y,
        duration: 1.4,
        ease: "power3.inOut",
        delay: 0.2,
        onUpdate: () => peel && peel.setPeelPosition(peelProxy.x, peelProxy.y),
        onComplete: () => {
          if (isClosed) return
          attachInteractionHandlers(restPos, hoverPos)
        },
      })
    }
  })
}
